"use strict";

exports.__esModule = true;
exports.SessionVOD = void 0;
var _Event = require("../internal/Event");
var _YoLog = require("../Utils/YoLog");
var _Session = require("./Session");
var _Parser = require("../Parsers/Parser");
var _StringUtils = require("../Utils/StringUtils");
var _PlaybackEventHandler = require("./PlaybackEventHandler");
var _HttpConnection = require("../Utils/HttpConnection");
var _HttpUtils = require("../Utils/HttpUtils");
var _SessionSeekable2 = require("../internal/SessionSeekable");
var _DefaultPlaybackPolicyHandler = require("../internal/DefaultPlaybackPolicyHandler");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
var PATTERN_SCHEME = /(.+?:\/\/)/;

/**
 * Concrete Session implementation representing a single viewing of a single Video-on-Demand (VOD) stream sourced from
 * the Yospace Video Platform via the Yospace VOD access service
 * @see {@link Session} base class
 * @hideconstructor
 */
var SessionVOD = /*#__PURE__*/function (_SessionSeekable) {
  _inheritsLoose(SessionVOD, _SessionSeekable);
  /**
   * @callback sessionCallback 
   * @param {SessionVOD} session a completed session object
   */
  /**
   * The factory method for creating a SessionVOD instance. Constructs and initialises a new
   * SessionVOD instance using the {@link SessionProperties} provided. On completion invokes the
   * callback provided, passing back the initialised SessionVOD instance if a callback was provided.
   *
   * If a callback was not provided, a Promise is returned instead.
   *
   * @param {string} url The Yospace initialisation URL to the Central Streaming Manager
   * @param {SessionProperties} [properties] (Optional) An initialisation properties object
   * @param {sessionCallback} [callback] (Optional) A completion handler to call after initialisation is finished.
   */
  SessionVOD.create = function create(url, properties, callback) {
    var session = new SessionVOD(properties);
    if (callback) {
      session.initialiseSession(url).then(function () {
        _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_LIFECYCLE, "Session initialised");
      })["catch"](function () {
        _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_LIFECYCLE, "Session failed to initialise");
      })["finally"](function () {
        callback(new _Event.Event(session));
      });
    } else {
      return session.initialiseSession(url);
    }
  };
  function SessionVOD(properties) {
    var _this;
    _this = _SessionSeekable.call(this, properties) || this;
    _this.setPlaybackPolicyHandler(new _DefaultPlaybackPolicyHandler.DefaultPlaybackPolicyHandler(_Session.PlaybackMode.VOD));
    return _this;
  }

  /**
   * Provides a relative content playhead position to the client, discounting the sum of all ad break
   * durations prior to the absolute playhead position provided. This allows the client to return
   * to the same content position if a VOD stream is stopped before playback ends.
   *
   * @param {number} playhead the absolute playhead position, in milliseconds
   * @return {number} the relative content position, in milliseconds
   * @see {@link SessionVOD}.getPlayheadForContentPosition()
   */
  var _proto = SessionVOD.prototype;
  _proto.getContentPositionForPlayhead = function getContentPositionForPlayhead(playhead) {
    if (this.duration === 0 || playhead === 0) {
      return 0;
    }
    if (this.linearAdBreaks.length === 0) {
      return playhead;
    }

    // Adjust playhead to end of break if playhead is within a break
    for (var _iterator = _createForOfIteratorHelperLoose(this.linearAdBreaks), _step; !(_step = _iterator()).done;) {
      var ab = _step.value;
      if (ab.getStart() <= playhead && playhead < ab.getStart() + ab.getDuration()) {
        playhead = ab.getStart() + ab.getDuration();
        break;
      }
    }
    var index = 0;
    var contentPlayhead = playhead;
    while (index < this.linearAdBreaks.length && this.linearAdBreaks[index].getStart() < playhead) {
      contentPlayhead -= this.linearAdBreaks[index++].getDuration();
    }
    return contentPlayhead;
  };
  _proto.getPlaybackMode = function getPlaybackMode() {
    return _Session.PlaybackMode.VOD;
  }

  /**
   * Provides an absolute playhead position to the client calculating the sum of all ad break durations
   * prior to that absolute playhead position plus the relative content playhead position.
   * This allows the client to return to the same content position if a VOD stream is stopped
   * before playback ends.
   *
   * @param {number} position the relative content position, in milliseconds
   * @return {number} the absolute playhead position, in milliseconds
   * @see {@link SessionVOD}.getContentPositionForPlayhead()
   */;
  _proto.getPlayheadForContentPosition = function getPlayheadForContentPosition(position) {
    if (this.linearAdBreaks.length === 0) {
      return position;
    }
    var index = 0;
    while (index < this.linearAdBreaks.length && this.linearAdBreaks[index].getStart() <= position) {
      position += this.linearAdBreaks[index++].getDuration();
    }
    return position;
  };
  _proto.initialiseSession = function initialiseSession(url) {
    var _this2 = this;
    return new Promise(function (resolve, reject) {
      _YoLog.YoLog.trace("sdk init");
      if (!(0, _HttpUtils.getUrl)(url)) {
        _this2.completeWithStatus(_Session.SessionState.FAILED, _Session.MALFORMED_URL);
        reject(_this2);
        return;
      }

      // Request the access URL
      _HttpConnection.HttpConnection.get(url, {
        method: "GET"
      }, _this2.sessionProperties.getRequestTimeout(), _this2.sessionProperties.getCustomHttpHeaders()).then(function (response) {
        if (response.ok) {
          var contentType = response.headers.get("content-type");
          if (contentType && contentType.toLowerCase().includes("application/json")) {
            if (_this2.handleFallbackUrl(response.body)) {
              resolve(_this2);
            } else {
              reject(_this2);
            }
          } else {
            // Parse the VMAP response
            _Parser.Parser.parse(response.body, _this2).then(function (vmapResponse) {
              if (!vmapResponse || vmapResponse.getStreamDuration() === 0 || (0, _StringUtils.isNullOrEmpty)(vmapResponse.getHostNode() || (0, _StringUtils.isNullOrEmpty)(vmapResponse.getHostSuffix()))) {
                _this2.setPlaybackUrl(url);
                _this2.logVmapParseWarning(vmapResponse);
                if (!vmapResponse) {
                  _this2.completeWithStatus(_Session.SessionState.NO_ANALYTICS, _Session.UNKNOWN_FORMAT);
                  resolve(_this2);
                } else {
                  _this2.completeWithStatus(_Session.SessionState.NO_ANALYTICS, 0);
                  resolve(_this2);
                }
              } else if (vmapResponse.getHasPlaceholder()) {
                _this2.handlePlaceholderBreaks(vmapResponse, url);
                _this2.completeWithStatus(_Session.SessionState.INITIALISED, _Session.PARTIAL_VMAP);
                resolve(_this2);
              } else {
                // Set the session properties
                _this2.initialiseFromAnalyticData(vmapResponse, _this2.findScheme(url));
                _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_PARSING, vmapResponse.toString());
                _this2.completeWithStatus(_Session.SessionState.INITIALISED, 0);
                resolve(_this2);
              }
            });
          }
        } else {
          _YoLog.YoLog.e("VMAP request failed.  URL: " + url + ", status: " + response.status + ", error: " + response.statusText);
          _this2.completeWithStatus(_Session.SessionState.FAILED, response.status);
          reject(_this2);
        }
      });
    });
  };
  _proto.logVmapParseWarning = function logVmapParseWarning(vmapResponse) {
    var warning = "Unable to parse VMAP data.\n - Stream duration: " + (vmapResponse ? vmapResponse.getStreamDuration() : "null") + "\n - Host node: " + (vmapResponse ? vmapResponse.getHostNode() : "null") + "\n - Host suffix: " + (vmapResponse ? vmapResponse.getHostSuffix() : null);
    _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_LIFECYCLE, warning);
  };
  _proto.findScheme = function findScheme(url) {
    if (PATTERN_SCHEME.exec(url)) {
      var arr = PATTERN_SCHEME.exec(url);
      return arr[1];
    } else {
      _YoLog.YoLog.w("Unable to match scheme in primary URL.  Assuming http://");
      return "http://";
    }
  };
  _proto.handlePlaceholderBreaks = function handlePlaceholderBreaks(vmapResponse, url) {
    var _this3 = this;
    _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_LIFECYCLE, "Found partial VMAP");
    this.initialiseFromAnalyticData(vmapResponse, this.findScheme(url), true);
    var abs = vmapResponse.getLinearAdBreaks();

    // This isn't expected to happen and is intended for future-proofing in the event of changes elsewhere
    if (!abs.length) {
      _YoLog.YoLog.e("Fulfilment payload contains no breaks");
      this.onSessionError(_Session.SessionErrorCode.UNRESOLVED_BREAK);
      return;
    }

    // For VOD, all followup URLs are the same so just take the last one
    var followupUrl = abs[abs.length - 1].getAdTagUri();
    if (!(0, _HttpUtils.getUrl)(followupUrl)) {
      _YoLog.YoLog.e("Fulfilment payload followup URL is not valid: " + followupUrl);
      this.onSessionError(_Session.SessionErrorCode.UNRESOLVED_BREAK);
      return;
    }

    // Call out and amalgamate the partial vmap data
    _HttpConnection.HttpConnection.get(followupUrl, {
      method: "GET"
    }, this.sessionProperties.getRequestTimeout(), this.sessionProperties.getCustomHttpHeaders()).then(function (response) {
      if (response.ok) {
        _Parser.Parser.parse(response.body, _this3, {
          skipNormalisation: true
        }).then(function (vmapResponse) {
          if (vmapResponse) {
            if (_this3.mergePlaceholderBreaks(vmapResponse)) {
              if (_this3.reportsManager) {
                _this3.reportsManager.raiseAnalyticUpdateCallback(_this3);
              }
            } else {
              _YoLog.YoLog.e("Fulfilment payload could not be merged");
              _this3.onSessionError(_Session.SessionErrorCode.UNRESOLVED_BREAK);
            }
          } else {
            _YoLog.YoLog.e("Fulfilment payload could not be parsed");
            _this3.onSessionError(_Session.SessionErrorCode.UNRESOLVED_BREAK);
          }
        });
      } else {
        _YoLog.YoLog.e("Fulfilment payload request failed: " + response.statusText);
        _this3.onSessionError(_Session.SessionErrorCode.UNRESOLVED_BREAK);
      }
    });
    _YoLog.YoLog.d(_YoLog.DebugFlags.DEBUG_PARSING, vmapResponse.toString());
  };
  _proto.onPlayerEvent = function onPlayerEvent(event, playhead) {
    // Playback state has changed
    // Any non-linear playhead change must set this.didSeek
    if (event === _PlaybackEventHandler.PlayerEvent.SEEK || event === _PlaybackEventHandler.PlayerEvent.ADVERT_SKIP || event === _PlaybackEventHandler.PlayerEvent.ADVERT_REWIND) {
      this.didSeek = true;
    }
    _SessionSeekable.prototype.onPlayerEvent.call(this, event, playhead);
  };
  _proto.onPlayheadUpdate = function onPlayheadUpdate(playhead) {
    this.handleHeartbeat(playhead);
    _SessionSeekable.prototype.onPlayheadUpdate.call(this, playhead);
  }

  /**
   * Sets all adverts inactive in all ad breaks prior to the given playhead position. If the playhead
   * is within an advert then that advert is NOT marked as inactive. This method allows client applications
   * to seek to a position before playback begins.
   *
   * @param {number} playhead the playhead position
   */;
  _proto.setAdBreaksInactivePriorTo = function setAdBreaksInactivePriorTo(playhead) {
    _SessionSeekable.prototype.setAdBreaksInactivePriorTo.call(this, playhead);
  };
  _proto.setDuration = function setDuration(duration) {
    this.duration = duration;
  };
  return SessionVOD;
}(_SessionSeekable2.SessionSeekable);
exports.SessionVOD = SessionVOD;